import type { Position } from "@kundeninfo/api-client";

/**
 * Texte des Positions-Editors zu Zeilenart und Gliederung (Spec „Überschriften mit Ebene“, Dirk 03./04.11.2026):
 * Schlüssel englisch, Werte deutsch (Hausregel „Texte in Properties“). Die Ebene heisst in der Oberfläche 1–3, im
 * Datenmodell 0–2.
 */
const POSITION_KIND: Readonly<Record<Position["kind"], string>> =
  { HEADING: "Überschrift", ITEM: "Position", SUBTOTAL: "Zwischensumme", TEXT: "Textzeile" };
/** Der Name einer frisch eingefügten Zeile — ITEM, TEXT und HEADING brauchen einen (sonst Feldfehler). */
const NEW_NAME: Readonly<Record<Position["kind"], string>> =
  { HEADING: "Neue Überschrift", ITEM: "Neue Position", SUBTOTAL: "Zwischensumme", TEXT: "Text" };
/** Namen in Pille, Quittung und Ansage kürzen (eine Zeile, die Pille steht am Geist). */
const NAME_MAX = 28;
const gekuerzt = (name: string): string => {
  const zeile = name.trim().replace(/\s+/g, " ");
  return zeile.length > NAME_MAX ? `${zeile.slice(0, NAME_MAX - 1)}…` : zeile;
};

export const POSITION_TEXT = {
  kind: POSITION_KIND,
  newName: NEW_NAME,
  level: (level: number) => `Ebene ${level + 1}`,
  levelGroup: "Ebene",
  levelOfRow: (nummer: number) => `Ebene Zeile ${nummer}`,
  indent: (nummer: number) => `Zeile ${nummer} einrücken`,
  outdent: (nummer: number) => `Zeile ${nummer} ausrücken`,
  /** Ziehen am Griff (Entscheide 847–849, me-design `ziehbar`): Griff, Name in Quittung und Ansage, Vorschau-Pille. */
  drag: {
    grip: (nummer: number) => `Position ${nummer} verschieben`,
    /** „Position „Beratung““ — die Quittung des Helfers macht daraus „… an Position 3 verschoben.“ Ein leerer Name
     *  oder der Standardname der Art („Zwischensumme“, „Neue Position“) sagt nichts: dann Art und Nummer. */
    name: (kind: Position["kind"], name: string, nummer: number) => {
      const n = name.trim();
      return n === "" || n === POSITION_KIND[kind] || n === NEW_NAME[kind]
        ? `${POSITION_KIND[kind]} ${nummer}` : `${POSITION_KIND[kind]} „${gekuerzt(n)}“`;
    },
    target: (nummer: number, vorgaenger: string | null) =>
      (vorgaenger === null ? `→ Pos. ${nummer} · an den Anfang` : `→ Pos. ${nummer} · nach „${gekuerzt(vorgaenger)}“`),
    busy: "Die Positionen speichern gerade — gleich noch einmal",
  },
} as const;

/** Die Ebenen einer Überschrift im Datenmodell (Domäne: 0..2). */
export const HEADING_LEVELS = [0, 1, 2] as const;
